import _memoize from 'lodash/memoize';
import { FONT, getFont } from '@tornado-rdk/typography';
import { FULL_HD, HD } from '@bedrock-rdk/constants';
import { PRIMARY, makeThemeColor } from '@tornado-rdk/theme';
import { colors as progressColors, HEIGHT as PROGRESS_HEIGHT } from '../progress/progress.style';
import { FOCUSED_Z_INDEX, UNFOCUSED_Z_INDEX, cacheResolver, makeDefaultStylingContext } from '../utils/style';
import {
  BORDER_RADIUS,
  BORDER_WIDTH,
  SCALE_FOCUSED,
  FOCUSED_BORDER_ALPHA,
  UNFOCUSED_BORDER_ALPHA,
} from '../utils/focus';
import { TRANSPARENT } from '../utils/colors';
import { middle } from '../utils/sizes';
import {
  EXTRA_TITLE_FONT_NAME,
  TITLE_FONT_NAME,
  TITLE_FONT_TYPE,
  DISPLAY_ICONS,
  BORDER_COLOR,
  POSTER_OVERLAY,
  HIGHLIGHT_COLOR,
  ICONS,
  INNER_BORDER_COLOR,
} from './poster.constants';

export const defaultContext = makeDefaultStylingContext();

export const PADDING_TOP = {
  [HD]: 4,
  [FULL_HD]: 4,
};

export const PADDING_RIGHT = {
  [HD]: 12,
  [FULL_HD]: 16,
};

export const PADDING_LEFT = {
  [HD]: 12,
  [FULL_HD]: 16,
};

export const PADDING_BOTTOM = {
  [HD]: 8,
  [FULL_HD]: 12,
};

export const ICON_PADDING_VERTICAL = 4;

export const ICON_PADDING_HORIZONTAL = 8;

export const ICON_HEIGHT = {
  [HD]: 24,
  [FULL_HD]: 32,
};

export const IMAGE_OFFSET = {
  [HD]: BORDER_WIDTH[HD] * 2,
  [FULL_HD]: BORDER_WIDTH[FULL_HD] * 2,
};

export const getDetailsPosition = _memoize(
  (screenSize, hasHighlightText = true, hasCsa = true) =>
    hasHighlightText
      ? {
          mountX: 1,
          x: x => x - PADDING_RIGHT[screenSize] - (hasCsa ? ICON_HEIGHT[screenSize] + ICON_PADDING_HORIZONTAL : 0),
        }
      : {
          mountX: 0,
          x: PADDING_LEFT[screenSize],
        },
  (screenSize, hasHighlightText, hasCsa) => `${screenSize}_${hasHighlightText}_${hasCsa}`,
);

export const getIcon = (icons, position) => (icons ? icons[ICONS[position]] : null);

export const colors = {
  border: BORDER_COLOR,
  innerBorder: INNER_BORDER_COLOR,
  text: makeThemeColor(PRIMARY),
  details: makeThemeColor(PRIMARY, 0.6),
  highlight: HIGHLIGHT_COLOR,
};

export const getInitialStyle = (context = defaultContext) => {
  const { screenSize, colorMode } = context;

  const titleFont = getFont(TITLE_FONT_NAME, screenSize, TITLE_FONT_TYPE);
  const extraTitleFont = getFont(EXTRA_TITLE_FONT_NAME, screenSize);
  const commonFont = getFont(FONT.BERLIN_1, screenSize);
  const commonTextStyle = {
    verticalAlign: 'middle',
    maxLines: 1,
    textOverflow: 'ellipsis',
    maxLinesSuffix: '...',
    wordWrap: false,
  };

  return {
    zIndex: UNFOCUSED_Z_INDEX,
    Border: {
      alpha: 0,
      x: -BORDER_WIDTH[screenSize],
      y: -BORDER_WIDTH[screenSize],
      color: TRANSPARENT,
      shader: {
        radius: BORDER_RADIUS[screenSize],
        outline: BORDER_WIDTH[screenSize],
        strokeColor: TRANSPARENT,
        innerColor: TRANSPARENT,
      },
    },
    Image: {
      shader: {
        radius: BORDER_RADIUS[screenSize],
        progressHeight: PROGRESS_HEIGHT,
        progressBackgroundColor: progressColors.background[colorMode],
        progressBarColor: progressColors.bar[colorMode],
        overlayColor: POSTER_OVERLAY[colorMode],
      },
    },
    ExtraTitle: {
      text: {
        ...extraTitleFont,
        ...commonTextStyle,
      },
      color: colors.text[colorMode],
    },
    Title: {
      text: {
        ...titleFont,
        ...commonTextStyle,
      },
      color: colors.text[colorMode],
    },
    Details: {
      ...getDetailsPosition(screenSize),
      text: {
        ...commonFont,
        ...commonTextStyle,
      },
      alpha: 0.6,
      color: colors.details[colorMode],
    },
    Highlights: {
      text: {
        ...commonFont,
        ...commonTextStyle,
      },
      alpha: 0.6,
      color: colors.highlight[colorMode],
    },
    TopLeftIcon: {
      visible: DISPLAY_ICONS,
      x: ICON_PADDING_HORIZONTAL,
      y: ICON_PADDING_VERTICAL,
    },
    TopRightIcon: {
      visible: DISPLAY_ICONS,
      mountX: 1,
      x: w => w - ICON_PADDING_HORIZONTAL,
      y: ICON_PADDING_VERTICAL,
    },
    BottomRightIcon: {
      visible: DISPLAY_ICONS,
      mountX: 1,
      x: w => w - ICON_PADDING_HORIZONTAL,
      y: h => h - PADDING_BOTTOM[screenSize] - ICON_HEIGHT[screenSize],
      alpha: 0.8,
    },
    Incentive: {
      x: middle,
      y: middle,
      mount: 0.5,
    },
  };
};

export const getIconViewBox = _memoize(screenSize => ({
  h: ICON_HEIGHT[screenSize],
}));

export const getFocusedStyle = _memoize(
  ({ colorMode }) => ({
    zIndex: FOCUSED_Z_INDEX,
    Border: {
      smooth: {
        alpha: FOCUSED_BORDER_ALPHA,
        scale: SCALE_FOCUSED,
        'shader.strokeColor': colors.border[colorMode],
        'shader.innerColor': colors.innerBorder[colorMode],
        'shader.scale': SCALE_FOCUSED,
      },
    },
    Image: {
      smooth: {
        scale: SCALE_FOCUSED,
        'shader.scale': SCALE_FOCUSED,
      },
    },
  }),
  cacheResolver,
);

export const unFocusedStyle = {
  zIndex: UNFOCUSED_Z_INDEX,
  Border: {
    smooth: {
      alpha: UNFOCUSED_BORDER_ALPHA,
      scale: 1,
      'shader.strokeColor': TRANSPARENT,
      'shader.innerColor': TRANSPARENT,
      'shader.scale': 1.0,
    },
  },
  Image: {
    smooth: {
      scale: 1,
      'shader.scale': 1.0,
    },
  },
};
